import React from 'react';
import { X } from 'lucide-react';
import { FilterCondition, FilterConfig } from './types';

interface FilterConditionRowProps {
  condition: FilterCondition;
  availableFilters: FilterConfig[];
  onUpdate: (condition: FilterCondition) => void;
  onRemove: () => void;
  showRemove: boolean;
}

export const FilterConditionRow: React.FC<FilterConditionRowProps> = ({
  condition,
  availableFilters,
  onUpdate,
  onRemove,
  showRemove,
}) => {
  const selectedFilter = availableFilters.find((f) => f.field === condition.field);

  const handleFieldChange = (newField: string) => {
    const newFilter = availableFilters.find((f) => f.field === newField);
    if (!newFilter) return;

    // Reset operator and value when field changes
    onUpdate({
      ...condition,
      field: newField,
      operator: newFilter.operators[0]?.value || '=',
      value: newFilter.valueType === 'boolean' ? true : '',
    });
  };

  const handleOperatorChange = (newOperator: string) => {
    onUpdate({
      ...condition,
      operator: newOperator,
    });
  };

  const handleValueChange = (newValue: any) => {
    onUpdate({
      ...condition,
      value: newValue,
    });
  };

  const renderValueInput = () => {
    if (!selectedFilter) return null;

    if (selectedFilter.valueOptions === 'input') {
      // Text or number input
      return (
        <input
          type={selectedFilter.valueType === 'number' ? 'number' : 'text'}
          value={condition.value || ''}
          onChange={(e) => handleValueChange(
            selectedFilter.valueType === 'number'
              ? parseFloat(e.target.value)
              : e.target.value
          )}
          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter value..."
        />
      );
    } else {
      // Dropdown select
      return (
        <select
          value={condition.value}
          onChange={(e) => {
            const value = selectedFilter.valueType === 'boolean'
              ? e.target.value === 'true'
              : selectedFilter.valueType === 'number'
              ? parseFloat(e.target.value)
              : e.target.value;
            handleValueChange(value);
          }}
          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {selectedFilter.valueOptions.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Field Selector */}
      <select
        value={condition.field}
        onChange={(e) => handleFieldChange(e.target.value)}
        className="w-40 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {availableFilters.map((filter) => (
          <option key={filter.field} value={filter.field}>
            {filter.label}
          </option>
        ))}
      </select>

      {/* Operator Selector */}
      <select
        value={condition.operator}
        onChange={(e) => handleOperatorChange(e.target.value)}
        className="w-16 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {selectedFilter?.operators.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>

      {/* Value Input */}
      {renderValueInput()}

      {/* Remove Button */}
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          title="Remove filter"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
